import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  FormControlLabel,
  Switch,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Grid,
  InputAdornment,
  Alert
} from '@mui/material';
import {
  FilterList as FilterListIcon,
  Group as GroupIcon,
  WaterDrop as WaterDropIcon,
  Percent as PercentIcon,
  Close as CloseIcon,
  FilterAlt as FilterAltIcon,
  Warning as WarningIcon
} from '@mui/icons-material';

const FilterModal = ({
  open,
  onClose,
  onApply,
  onReset,
  includeUnregistered,
  setIncludeUnregistered,
  groups,
  selectedGroupIds,
  setSelectedGroupIds,
  selectedProduct,
  allFilters
}) => {
  // 🚀 修正: 無限ループを防ぐため初期化ロジックを改善
  const [localIncludeUnregistered, setLocalIncludeUnregistered] = useState(includeUnregistered);
  const [localSelectedGroups, setLocalSelectedGroups] = useState(selectedGroupIds);

  // 残量フィルター用state
  const [remainingFilters, setRemainingFilters] = useState({
    remainingMin: '',
    remainingMax: '',
    ratioMin: '',
    ratioMax: '',
    hideIncompleteData: false
  });

  const [errors, setErrors] = useState({});

  // バリデーションタイマーの参照を適切に管理
  const validationTimerRef = useRef(null);
  
  // 🚀 追加: 初期化フラグで無限ループを防止
  const isInitializedRef = useRef(false);

  // 「すべて」が選択されているかどうか
  const isAllSelected = !selectedProduct || selectedProduct === 'all';

  // 🚀 修正: useEffectの条件を厳格化して無限ループを防止
  useEffect(() => {
    // モーダルが開かれた時のみ初期化
    if (open && !isInitializedRef.current) {
      setLocalIncludeUnregistered(includeUnregistered);
      setLocalSelectedGroups(selectedGroupIds);
      
      if (allFilters) {
        setRemainingFilters({
          remainingMin: allFilters.remainingMin || '',
          remainingMax: allFilters.remainingMax || '',
          ratioMin: allFilters.ratioMin || '',
          ratioMax: allFilters.ratioMax || '',
          hideIncompleteData: !includeUnregistered
        });
      }
      
      isInitializedRef.current = true;
    }
    
    // モーダルが閉じられた時に初期化フラグをリセット
    if (!open) {
      isInitializedRef.current = false;
    }
  }, [open, includeUnregistered, selectedGroupIds, allFilters]);

  // 「すべて」選択時の状態リセット
  useEffect(() => {
    if (isAllSelected && isInitializedRef.current) {
      setRemainingFilters(prev => ({
        remainingMin: '',
        remainingMax: '',
        ratioMin: '',
        ratioMax: '',
        hideIncompleteData: false
      }));
      setErrors({});
    }
  }, [isAllSelected]);

  // 完全なクリーンアップ処理
  useEffect(() => {
    return () => {
      if (validationTimerRef.current) {
        clearTimeout(validationTimerRef.current);
        validationTimerRef.current = null;
      }
    };
  }, []);

  // バリデーション
  const validateFilters = useCallback(() => {
    const newErrors = {};

    // 「すべて」選択時はバリデーションをスキップ
    if (isAllSelected) {
      setErrors({});
      return true;
    }

    // 残量フィルターのバリデーション
    if (remainingFilters.remainingMin && remainingFilters.remainingMax) {
      const min = parseFloat(remainingFilters.remainingMin);
      const max = parseFloat(remainingFilters.remainingMax);
      if (min > max) {
        newErrors.remaining = '最小値は最大値より小さい値を入力してください';
      }
    }

    // 割合フィルターのバリデーション
    if (remainingFilters.ratioMin && remainingFilters.ratioMax) {
      const min = parseFloat(remainingFilters.ratioMin);
      const max = parseFloat(remainingFilters.ratioMax);
      if (min > max) {
        newErrors.ratio = '最小値は最大値より小さい値を入力してください';
      }
    }

    // 割合の範囲チェック（0-100%）
    if (remainingFilters.ratioMin && (parseFloat(remainingFilters.ratioMin) < 0 || parseFloat(remainingFilters.ratioMin) > 100)) {
      newErrors.ratioMin = '0から100の値を入力してください';
    }
    if (remainingFilters.ratioMax && (parseFloat(remainingFilters.ratioMax) < 0 || parseFloat(remainingFilters.ratioMax) > 100)) {
      newErrors.ratioMax = '0から100の値を入力してください';
    }

    // 残量の負の値チェック
    if (remainingFilters.remainingMin && parseFloat(remainingFilters.remainingMin) < 0) {
      newErrors.remainingMin = '0以上の値を入力してください';
    }
    if (remainingFilters.remainingMax && parseFloat(remainingFilters.remainingMax) < 0) {
      newErrors.remainingMax = '0以上の値を入力してください';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [remainingFilters, isAllSelected]);

  // グループ選択の切り替え
  const handleGroupToggle = (groupId) => {
    const currentIndex = localSelectedGroups.indexOf(groupId);
    const newSelected = [...localSelectedGroups];

    if (currentIndex === -1) {
      newSelected.push(groupId);
    } else {
      newSelected.splice(currentIndex, 1);
    }

    setLocalSelectedGroups(newSelected);
  };

  // より安全なタイマー管理
  const handleRemainingFilterChange = useCallback((field, value) => {
    const newFilters = { ...remainingFilters, [field]: value };
    setRemainingFilters(newFilters);
    
    // 既存のタイマーをクリア
    if (validationTimerRef.current) {
      clearTimeout(validationTimerRef.current);
      validationTimerRef.current = null;
    }
    
    // 新しいタイマーを設定
    validationTimerRef.current = setTimeout(() => {
      if (validationTimerRef.current) {
        validateFilters();
        validationTimerRef.current = null;
      }
    }, 100);
  }, [remainingFilters, validateFilters]);

  // 🚀 フィルター適用（sessionStorage対応）
  const handleApply = useCallback(() => {
    if (!validateFilters()) {
      return;
    }

    const filters = {
      includeUnregistered: !remainingFilters.hideIncompleteData,
      selectedGroups: localSelectedGroups,
      ...(isAllSelected ? {} : {
        remainingMin: remainingFilters.remainingMin,
        remainingMax: remainingFilters.remainingMax,
        ratioMin: remainingFilters.ratioMin,
        ratioMax: remainingFilters.ratioMax,
        hideNoData: remainingFilters.hideIncompleteData
      })
    };

    
    onApply(filters);
    onClose();
  }, [validateFilters, remainingFilters, localSelectedGroups, isAllSelected, onApply, onClose]);

  // フィルターリセット
  const handleReset = useCallback(() => {
    setLocalSelectedGroups([]);
    setRemainingFilters({
      remainingMin: '',
      remainingMax: '',
      ratioMin: '',
      ratioMax: '',
      hideIncompleteData: false
    });
    setErrors({});
    onReset();
    onClose();
  }, [onReset, onClose]);

  // モーダル閉じ処理でクリーンアップを確実に実行
  const handleClose = useCallback(() => {
    // タイマーのクリーンアップ
    if (validationTimerRef.current) {
      clearTimeout(validationTimerRef.current);
      validationTimerRef.current = null;
    }
    onClose();
  }, [onClose]);

  // アクティブフィルター数のカウント
  const getActiveFilterCount = () => {
    let count = 0;
    
    // データ不完全フィルター
    if (remainingFilters.hideIncompleteData) count++;
    
    // グループフィルター
    if (localSelectedGroups.length > 0) count++;
    
    // 「すべて」選択時は残量フィルターをカウントしない
    if (!isAllSelected) {
      const remainingFilterKeys = ['remainingMin', 'remainingMax', 'ratioMin', 'ratioMax'];
      remainingFilterKeys.forEach(key => {
        if (remainingFilters[key] && remainingFilters[key] !== '') {
          count++;
        }
      });
    }
    
    return count;
  };

  const hasErrors = Object.keys(errors).length > 0;
  const activeFilterCount = getActiveFilterCount();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { 
          borderRadius: 2,
          m: { xs: 1, sm: 2 },
          maxHeight: { xs: '95vh', sm: '90vh' }
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterListIcon color="primary" />
          <Typography variant="h6" fontWeight="medium">
            絞り込み設定
            {activeFilterCount > 0 && (
              <Box
                component="span"
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  borderRadius: '50%',
                  width: 24,
                  height: 24,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  fontWeight: 'bold',
                  ml: 1
                }}
              >
                {activeFilterCount}
              </Box>
            )}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          表示する顧客の条件を設定してください
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 1, px: { xs: 2, sm: 3 } }}>
        {/* 「すべて」選択時の警告表示 */}
        {isAllSelected && (
          <Alert 
            severity="warning" 
            icon={<WarningIcon />}
            sx={{ mb: 2 }}
          >
            「すべて表示」選択時は、残量に関するフィルターは利用できません。特定の製品を選択してください。
          </Alert>
        )}

        {/* 基本フィルター */}
        <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 'medium' }}>
          基本フィルター
        </Typography>
        
        <FormControlLabel
          control={
            <Switch
              checked={remainingFilters.hideIncompleteData}
              onChange={(e) => handleRemainingFilterChange('hideIncompleteData', e.target.checked)}
              disabled={isAllSelected}
            />
          }
          label="データ不完全な顧客を非表示"
          sx={{ 
            mb: 1,
            '& .MuiFormControlLabel-label': {
              color: isAllSelected ? 'text.disabled' : 'text.primary',
              fontSize: { xs: '0.875rem', sm: '1rem' },
              lineHeight: 1.2
            }
          }}
        />
        
        <Typography 
          variant="caption" 
          color="text.secondary" 
          sx={{ 
            mb: 2, 
            display: 'block', 
            ml: 4,
            fontSize: { xs: '0.75rem', sm: '0.8125rem' }
          }}
        >
          {isAllSelected 
            ? '特定の製品を選択すると利用できます（すべて表示時は自動的にOFFになります）'
            : '未登録顧客・残量データ未設定の顧客を除外'
          }
        </Typography>

        <Divider sx={{ my: 2 }} />

        {/* グループフィルター */}
        <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 'medium' }}>
          <GroupIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
          グループフィルター
        </Typography>
        
        {groups.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            グループが登録されていません
          </Typography>
        ) : (
          <List dense sx={{ maxHeight: 150, overflow: 'auto', mb: 2 }}>
            {groups.map((group) => (
              <ListItem key={group.id} disablePadding>
                <ListItemButton onClick={() => handleGroupToggle(group.id)}>
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={localSelectedGroups.indexOf(group.id) !== -1}
                      tabIndex={-1}
                      disableRipple
                    />
                  </ListItemIcon>
                  <ListItemText primary={group.name} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}

        {/* 残量フィルター（「すべて」選択時は無効化） */}
        {!isAllSelected && (
          <>
            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 'medium' }}>
              <WaterDropIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
              残量フィルター
              {selectedProduct && (
                <Typography component="span" variant="caption" sx={{ ml: 1, opacity: 0.7 }}>
                  ({selectedProduct.name})
                </Typography>
              )}
            </Typography>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="残量最小値"
                  type="number"
                  value={remainingFilters.remainingMin}
                  onChange={(e) => handleRemainingFilterChange('remainingMin', e.target.value)}
                  placeholder="例: 50"
                  inputProps={{ min: 0, step: 0.1 }}
                  error={!!errors.remainingMin}
                  helperText={errors.remainingMin}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">L</InputAdornment>
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="残量最大値"
                  type="number"
                  value={remainingFilters.remainingMax}
                  onChange={(e) => handleRemainingFilterChange('remainingMax', e.target.value)}
                  placeholder="例: 200"
                  inputProps={{ min: 0, step: 0.1 }}
                  error={!!errors.remainingMax}
                  helperText={errors.remainingMax}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">L</InputAdornment>
                  }}
                />
              </Grid>
            </Grid>

            {errors.remaining && (
              <Typography variant="caption" color="error" sx={{ mb: 1, display: 'block' }}>
                {errors.remaining}
              </Typography>
            )}

            <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 'medium' }}>
              <PercentIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
              残量割合フィルター
            </Typography>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="割合最小値"
                  type="number"
                  value={remainingFilters.ratioMin}
                  onChange={(e) => handleRemainingFilterChange('ratioMin', e.target.value)}
                  placeholder="例: 10"
                  inputProps={{ min: 0, max: 100, step: 0.1 }}
                  error={!!errors.ratioMin}
                  helperText={errors.ratioMin}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="割合最大値"
                  type="number"
                  value={remainingFilters.ratioMax}
                  onChange={(e) => handleRemainingFilterChange('ratioMax', e.target.value)}
                  placeholder="例: 30"
                  inputProps={{ min: 0, max: 100, step: 0.1 }}
                  error={!!errors.ratioMax}
                  helperText={errors.ratioMax}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>
                  }}
                />
              </Grid>
            </Grid>

            {errors.ratio && (
              <Typography variant="caption" color="error" sx={{ mb: 1, display: 'block' }}>
                {errors.ratio}
              </Typography>
            )}
          </>
        )}

        {/* ヘルプテキスト */}
        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            <strong>フィルターのヒント:</strong><br />
            {isAllSelected ? (
              <>
                • グループフィルター: 特定のグループに属する顧客のみ表示<br />
                • データ不完全フィルターは「すべて表示」時は無効化されます<br />
                • 残量フィルターを使用するには、特定の製品を選択してください
              </>
            ) : (
              <>
                • データ不完全: 未登録顧客・残量データ未設定の顧客を除外<br />
                • 残量フィルター: 指定した範囲内の残量を持つ顧客のみ表示<br />
                • 割合フィルター: 容量に対する残量の割合で絞り込み<br />
                • すべてのフィルターを組み合わせて使用可能
              </>
            )}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions 
        sx={{ 
          px: { xs: 2, sm: 3 }, 
          pb: { xs: 2, sm: 3 },
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 1 },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: { xs: 'stretch', sm: 'flex-end' }
        }}
      >
        <Box 
          sx={{ 
            display: 'flex',
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            gap: 1,
            width: { xs: '100%', sm: 'auto' }
          }}
        >
          <Button
            onClick={handleReset}
            startIcon={<CloseIcon />}
            disabled={activeFilterCount === 0}
            size="small"
            sx={{
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              px: { xs: 2, sm: 2 },
              whiteSpace: 'nowrap',
              minWidth: { xs: 'auto', sm: '80px' }
            }}
          >
            リセット
          </Button>
          <Button
            onClick={handleClose}
            color="inherit"
            size="small"
            sx={{
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              px: { xs: 2, sm: 2 },
              whiteSpace: 'nowrap',
              minWidth: { xs: 'auto', sm: '80px' }
            }}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleApply}
            variant="contained"
            startIcon={<FilterAltIcon />}
            disabled={hasErrors}
            size="small"
            sx={{
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              px: { xs: 2, sm: 2 },
              whiteSpace: 'nowrap',
              minWidth: { xs: 'auto', sm: '80px' },
              fontWeight: 600
            }}
          >
            適用
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default FilterModal;